import { IncidentSubClassification } from '@models/Incident';
import { LanguageKey } from '@models/Language';
import { ROUTES, SATO_FI_ROUTES, SatoFIRoute } from '@routes/routes.constants';
import { SAUNA_ROUTES } from '@views/sauna/sauna.constants';

export type RouteMap = typeof ROUTES;
export type RouteName = keyof RouteMap;
export type RoutePath<T extends RouteName = RouteName> = RouteMap[T];

export type RouteParamsMap = {
  [SAUNA_ROUTES.SAUNA_PAGE]: { id: string };
  [SAUNA_ROUTES.SAUNA_RESERVATION_CALENDAR]: { id: string };
  [SAUNA_ROUTES.SAUNA_RESERVATION_CONFIRM]: { id: string };
};

/** Extract route params from a route string, i.e. params denoted as `:id` and so on.
 * Remake of ExtractRouteParams that react-router uses internally but doesn't export */
export type ExtractRouteParams<Path extends string> =
  Path extends `:${infer Param}/${infer Rest}`
    ? Param | ExtractRouteParams<Rest>
    : Path extends `:${infer Param}`
      ? Param
      : Path extends `${infer _Prefix}:${infer Rest}`
        ? ExtractRouteParams<`:${Rest}`>
        : never;

type ExtractOptionalRouteParams<Path extends string> =
  Extract<ExtractRouteParams<Path>, `${string}?`> extends `${infer Key}?`
    ? Key
    : never;

/** Infer route params from the given path string */
export type RouteParams<Path extends string> = Record<
  Exclude<ExtractRouteParams<Path>, `${string}?`>,
  string
> & {
  [key in ExtractOptionalRouteParams<Path>]?: string;
};

export enum QUERY_STRINGS {
  EMAIL = 'email',
  REDIRECT = 'redirect',
  VERIFICATION_CODE = 'verificationCode',
  /** FTN auth token */
  TOKEN = 'token',
  REGISTRATION_TYPE = 'registrationType',
  QUICKLINK_TOKEN = 'invitationToken',
  ERROR = 'error',
  ERROR_ID = 'errorId',
  METADATA = 'metadata',
  USERNAME = 'username',
  CRM_ID = 'userCrmId',
  LANGUAGE = 'language',
  REFRESH = 'refresh',
  MESSAGE_CATEGRY = 'category',
  PARKING_AREA_ID = 'areaId',
  PARKING_AREA_TYPE = 'areaType',
  PARKING_PRODUCT_ID = 'parkingProductId',
  PARKING_CONTRACT_ID = 'parkingContractId',
  SAUNA_SHIFT_TIME_RANGE = 'time',
  SAUNA_SHIFT_DAY_OF_WEEK = 'day',
  /** Current sauna shift time that the user wants to change from */
  CURRENT_SAUNA_SHIFT_TIME_RANGE = 'currentShiftTime',
  /** Current sauna shift weekday that the user wants to change from */
  CURRENT_SAUNA_SHIFT_DAY_OF_WEEK = 'currentShiftDay',
  SAUNA_ID = 'saunaId',
  FILTER = 'filter',
  MESSAGE_TITLE = 'title',
  MESSAGE_DESCRIPTION = 'description',
}

export type QueryParamTypeMap = {
  [QUERY_STRINGS.MESSAGE_CATEGRY]: IncidentSubClassification;
};

export type QueryParamType<T extends QUERY_STRINGS> =
  T extends keyof QueryParamTypeMap ? QueryParamTypeMap[T] : string;

export type SatoFiRouteParams<T extends SatoFIRoute> = RouteParams<
  (typeof SATO_FI_ROUTES)[T][LanguageKey]
>;
